import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Card, CardContent, CircularProgress, Grid, LinearProgress, Menu, MenuItem, Popover } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import ApplicationConf from 'conf/ApplicationConf'
import { aquaFetch } from 'common/utils/ActionUtils'
import useTitles from 'common/hooks/useTitles'
import useActions from 'common/hooks/useActions'
import { OTHER } from 'common/components/action/ActionConstant'
import { CardTitle } from 'common/components/card/Card'
import DateTimePicker from 'common/components/form/DateTimePicker'
import { computeRangeLabel } from 'common/utils/DateUtil'
import { isNil, mean, orderBy, round, uniq } from 'lodash'
import useFetch from 'common/hooks/useFetch'
import moment from 'moment'
import 'moment/locale/fr'
import PropTypes from 'prop-types'

const aggIcon = (label, active) => `image://data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"><text x="10" y="15" text-anchor="middle" font-family="Arial" font-size="14" font-weight="bold" fill="${active ? '#1976d2' : '#666'}">${label}</text></svg>`,
)}`

const AGGREGATIONS = [
    { value: null, label: 'H', name: 'Données horaires brutes' },
    { value: 'day', label: 'J', name: 'Moyenne par Jour' },
    { value: 'isoWeek', label: 'S', name: 'Moyenne par Semaine' },
    { value: 'month', label: 'M', name: 'Moyenne par Mois' },
    { value: 'year', label: 'A', name: 'Moyenne par Année' },
]

const formatBucket = (value, aggregation, withTime = false) => {
    const m = moment(value).locale('fr')
    if (aggregation === 'isoWeek') return `S${m.format('WW YYYY')}`
    if (aggregation === 'month') {
        const month = m.format('MMMM')
        return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${m.format('YYYY')}`
    }
    if (aggregation === 'year') return m.format('YYYY')
    if (aggregation === 'day') return m.format('DD/MM')
    return withTime ? m.format('DD/MM HH:mm') : m.format('DD/MM')
}

// Ratio « traitements par instance » : chaque série jobs_* divisée par instances_total relevé à la même date
// (les stats sont collectées ensemble, donc mêmes horodatages). Relevés sans total (ou à 0) écartés.
const RATIO_DECIMALS = 2

const buildJobsPerInstance = (jobsSeries = [], instancesSeries = []) => {
    const totals = Object.fromEntries((instancesSeries.find(s => s.name === 'instances_total')?.data ?? []).map(([date, value]) => [date, value]))
    return jobsSeries.map(serie => ({
        ...serie,
        name: `${serie.name} / instance`,
        data: serie.data
            .filter(([date]) => totals[date] > 0)
            .map(([date, value]) => [date, round(value / totals[date], RATIO_DECIMALS)]),
    }))
}

// ratioData (optionnel) : mêmes séries rapportées au nombre total d'instances ; la card propose alors de basculer
// entre le nombre réel et le ratio par instance.
const SupervisionChart = ({ group, rawData: countData, ratioData, forced, range }) => {
    const [aggregation, setAggregation] = useState(null)
    const [isRatio, setIsRatio] = useState(false)

    useEffect(() => {
        setAggregation(forced.value)
    }, [forced.version])

    const showRatio = isRatio && !isNil(ratioData)
    const rawData = showRatio ? ratioData : countData
    // Décimales gardées par la moyenne d'agrégation : entiers pour les comptages, 2 décimales pour le ratio.
    const decimals = showRatio ? RATIO_DECIMALS : 0

    const series = useMemo(() => {
        const [start, end] = range
        const hasRange = !isNil(start) || !isNil(end)
        const filtered = hasRange
            ? rawData.map(serie => ({
                ...serie,
                data: serie.data.filter(([date]) => {
                    const ts = typeof date === 'number' ? date : moment(date).valueOf()
                    if (!isNil(start) && ts < start) return false
                    if (!isNil(end) && ts > end) return false
                    return true
                }),
            }))
            : rawData
        if (!aggregation) return filtered
        return filtered.map(serie => {
            const buckets = new Map()
            serie.data.forEach(([date, value]) => {
                const bucket = moment(date).startOf(aggregation).valueOf()
                if (!buckets.has(bucket)) buckets.set(bucket, [])
                buckets.get(bucket).push(value)
            })
            const data = [...buckets.entries()]
                .sort(([a], [b]) => a - b)
                .map(([bucket, values]) => [bucket, round(mean(values), decimals)])
            return { ...serie, data }
        })
    }, [rawData, aggregation, range, decimals])

    // Options mémoïsées : le graphique n'est redessiné que si ses données, l'agrégation ou la plage changent (et pas à
    // chaque rendu de la page, ex. pendant le chargement progressif des autres groupes).
    const option = useMemo(() => {
        const toggle = (agg) => () => setAggregation(curr => curr === agg ? null : agg)
        return {
            // Pas d'animation ni de point sur chaque relevé (visible au survol seulement) : en données horaires brutes,
            // plusieurs centaines de points par courbe rendaient l'affichage et le survol très lents.
            animation: false,
            legend: { type: 'scroll', data: series.map(d => d.name), bottom: 0 },
            toolbox: {
                right: 10,
                top: 0,
                itemSize: 18,
                feature: {
                    myRaw: { show: true, title: 'Données horaires brutes', icon: aggIcon('H', aggregation === null), onclick: () => setAggregation(null) },
                    myDay: { show: true, title: 'Jour', icon: aggIcon('J', aggregation === 'day'), onclick: toggle('day') },
                    myWeek: { show: true, title: 'Semaine', icon: aggIcon('S', aggregation === 'isoWeek'), onclick: toggle('isoWeek') },
                    myMonth: { show: true, title: 'Mois', icon: aggIcon('M', aggregation === 'month'), onclick: toggle('month') },
                    myYear: { show: true, title: 'Année', icon: aggIcon('A', aggregation === 'year'), onclick: toggle('year') },
                },
            },
            xAxis: [{
                type: 'time',
                min: isNil(range[0]) ? undefined : range[0],
                max: isNil(range[1]) ? undefined : range[1],
                axisLabel: {
                    formatter(value) {
                        return formatBucket(value, aggregation)
                    },
                },
            }],
            yAxis: [{ type: 'value', scale: true }],
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    label: {
                        formatter({ value }) {
                            return formatBucket(value, aggregation, true)
                        },
                    },
                },
            },
            grid: { top: 30, right: 20, bottom: 50, left: 50, height: 'auto' },
            series: series.map(s => ({ ...s, showSymbol: false, sampling: 'lttb' })),
        }
    }, [series, aggregation, range])

    // Bascule nombre réel / ratio par instance, dans les actions de la card (graphiques qui ont un ratio).
    const cardActions = isNil(ratioData) ? [] : [{
        icon: showRatio ? 'numbers' : 'percent',
        tooltip: showRatio ? 'Afficher le nombre réel' : 'Afficher le ratio par instance',
        onClick: () => setIsRatio(v => !v),
    }]

    return (
        <Grid size={4}>
            <Card>
                <CardTitle title={showRatio ? `${group} par instance` : group} actions={cardActions} />
                <CardContent>
                    <ReactECharts
                        option={option}
                        notMerge={true}
                        lazyUpdate={true}
                        style={{ height: 'calc(50vh - 100px)' }}
                    />
                </CardContent>
            </Card>
        </Grid>
    )
}

SupervisionChart.propTypes = {
    group: PropTypes.string,
    rawData: PropTypes.array,
    forced: PropTypes.shape({
        value: PropTypes.string,
        version: PropTypes.number,
    }),
    range: PropTypes.arrayOf(PropTypes.number),
    ratioData: PropTypes.array,
}

const StatisticsSupervisionApp = () => {
    const [series, setSeries] = useState([])
    const [forced, setForced] = useState({ value: null, version: 0 })
    const [range, setRange] = useState([null, null])
    const [pendingRange, setPendingRange] = useState([null, null])
    const [aggMenuAnchor, setAggMenuAnchor] = useState(null)
    const [rangeMenuAnchor, setRangeMenuAnchor] = useState(null)

    const applyToAll = (value) => setForced(p => ({ value, version: p.version + 1 }))

    const onOpenAggMenu = (e) => setAggMenuAnchor(e.currentTarget)
    const onCloseAggMenu = () => setAggMenuAnchor(null)
    const onSelectAggregation = (value) => {
        applyToAll(value)
        onCloseAggMenu()
    }

    const onOpenRangeMenu = (e) => {
        setPendingRange(range)
        setRangeMenuAnchor(e.currentTarget)
    }
    const onCloseRangeMenu = () => setRangeMenuAnchor(null)

    const onApplyRange = () => {
        const [start, end] = pendingRange
        setRange([
            isNil(start) ? null : moment(start).startOf('day').valueOf(),
            isNil(end) ? null : moment(end).endOf('day').valueOf(),
        ])
        onCloseRangeMenu()
    }

    const onResetRange = () => {
        setPendingRange([null, null])
        setRange([null, null])
        onCloseRangeMenu()
    }

    useTitles(() => {
        const rangeLabel = computeRangeLabel(range, series)
        return [
            { label: 'Statistiques de supervision', href: '/statisticsSupervision' },
            ...(rangeLabel ? [{ label: rangeLabel, href: '/statisticsSupervision' }] : []),
        ]
    }, [range, series])

    useActions(() => [
        { type: OTHER, icon: 'event', tooltip: 'Plage de dates', onClick: onOpenRangeMenu },
        { type: OTHER, icon: 'tune', tooltip: 'Agrégation', onClick: onOpenAggMenu },
    ], [range])

    const { data: types = [] } = useFetch(ApplicationConf.supervision.types())

    const groups = useMemo(() => uniq(types.map(t => t.split('_')?.[0])), [types])

    // Calculé une fois par chargement (et pas à chaque rendu), dès que les groupes jobs et instances sont arrivés.
    const jobsPerInstance = useMemo(() => {
        const jobs = series.find(s => s.group === 'jobs')
        const instances = series.find(s => s.group === 'instances')
        return jobs && instances ? buildJobsPerInstance(jobs.data, instances.data) : undefined
    }, [series])

    useEffect(() => {
        if (!types.length) return
        setSeries([])
        let cancelled = false
        groups.forEach(async group => {
            const allSeries = await Promise.all(
                types.filter(t => t.startsWith(group)).map(async (type) => {
                    const data = await aquaFetch(ApplicationConf.supervision.statsByTypes(type))
                    return { name: type, type: 'line', data: data.map(d => [d.date, d.valeur]) }
                }),
            )
            if (!cancelled) {
                setSeries(s => [...s, { group, data: allSeries }])
            }
        })
        return () => {
            cancelled = true
        }
    }, [types])

    return (
        <>
            <Grid container spacing={1}>
                {!types.length && (
                    <Grid size={12}>
                        <Card>
                            <CardContent sx={{ padding: 3 }}>
                                <Grid container justifyContent='center' spacing={3}>
                                    <Grid size='auto'>Chargement en cours</Grid>
                                    <Grid size={12}>
                                        <LinearProgress variant='indeterminate' />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                {orderBy(groups).map(group => {
                    const groupSeries = series.find(s => s.group === group)
                    if (!groupSeries) {
                        return (
                            <Grid size={4} key={group}>
                                <Card>
                                    <CardTitle title={group} />
                                    <CardContent>
                                        <Box sx={{ height: 'calc(50vh - 100px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <CircularProgress />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )
                    }
                    return (
                        <SupervisionChart
                            key={group}
                            group={group}
                            rawData={groupSeries.data}
                            // Graphique jobs : bascule possible vers le ratio par instance.
                            ratioData={group === 'jobs' ? jobsPerInstance : undefined}
                            forced={forced}
                            range={range}
                        />
                    )
                })}
            </Grid>
            <Menu
                anchorEl={aggMenuAnchor}
                open={Boolean(aggMenuAnchor)}
                onClose={onCloseAggMenu}
            >
                {AGGREGATIONS.map(agg => (
                    <MenuItem
                        key={agg.label}
                        onClick={() => onSelectAggregation(agg.value)}
                        selected={forced.value === agg.value}
                    >
                        <Box
                            sx={{
                                width: 24,
                                marginRight: 1.5,
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: forced.value === agg.value ? 'primary.main' : 'text.secondary',
                            }}
                        >
                            {agg.label}
                        </Box>
                        {agg.name}
                    </MenuItem>
                ))}
            </Menu>
            <Popover
                anchorEl={rangeMenuAnchor}
                open={Boolean(rangeMenuAnchor)}
                onClose={onCloseRangeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { padding: 2, width: 380 } } }}
            >
                <Grid container spacing={1}>
                    <Grid size='grow'>
                        <DateTimePicker
                            label='Du'
                            value={pendingRange[0]}
                            onChange={v => setPendingRange(([, end]) => [
                                !isNil(end) && !isNil(v) ? Math.min(v, end) : v,
                                end,
                            ])}
                        />
                    </Grid>
                    <Grid size='auto' sx={{ marginTop: '18px' }}>-</Grid>
                    <Grid size='grow'>
                        <DateTimePicker
                            label='Au'
                            value={pendingRange[1]}
                            onChange={v => setPendingRange(([start]) => [
                                start,
                                !isNil(start) && !isNil(v) ? Math.max(v, start) : v,
                            ])}
                        />
                    </Grid>
                </Grid>
                <Grid container justifyContent='flex-end' spacing={1} sx={{ marginTop: 1 }}>
                    <Button onClick={onResetRange} variant='outlined' size='small'>Réinitialiser</Button>
                    <Button onClick={onApplyRange} variant='contained' size='small'>Appliquer</Button>
                </Grid>
            </Popover>
        </>
    )
}

export default StatisticsSupervisionApp
